import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import { MemberInfoValue } from '../../shared/component/headless/input/InputBody';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { BlackFullWidthBtn } from '../../shared/component/headless/button/BtnBody';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useValidation } from '../../shared/hooks/useValidation';
import Space from '../../shared/Space';
import { useMember } from '../../shared/hooks/useApi/useMember';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import searchIcon from '../../asset/icon/see.png';
import searchNotIcon from '../../asset/icon/see_not.png';
import { popIn } from '../../shared/component/Animation';
import deleteIcon from '../../asset/icon/delete.png';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { paperTextureModal } from '../../shared/component/paperTexture';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';

interface ModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const SetPWModal: React.FC<ModalProps> = ({ setIsUserInput }) => {
  const { user } = useAuth();
  const { standardModal, isClosing, clearModal } = useModalStore();
  const { setPassword } = useMember();
  const { 
    PASSWORD_REGEX,
    explainPasswordIssues
  } = useValidation();
  const [showPassword, setShowPassword] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [error, setError] = useState('');

  useEffect(() => {
    if (newPw.length > 0) {
      setIsUserInput(true);
    } else {
      setIsUserInput(false);
    }
  }, [newPw]);

  if (user == null || standardModal == null) return null;

  const handleSubmit = async () => {
    if (!validateNewPassword(newPw)) {
      return;
    }
    setError('');

    await setPassword({
      id: user.id,
      password: newPw,
    });
  };

  const validateNewPassword = (value: string) => {
    if (value.length === 0) {
      setError('');
      return true;
    }
    if (!PASSWORD_REGEX.test(value)) {
      const issues = explainPasswordIssues(value);
      setError(issues[0] ?? 'Password needs a little adjustment.');
      return false;
    }
    setError('');
    return true;
  };

  const changeNewPasswordInput = (value: string) => {
    validateNewPassword(value);
    setNewPw(value);
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <ChangeModalFrame
      onClick={(e) => e.stopPropagation()}
      $isClosing={isClosing}
    >
      <ChangeModalComp>
        <HeaderRow>
          <TitleGroup>
            <Title>{getModalMessage('SET_PASSWORD_TITLE')}</Title>
            <UniversalIconBtn
              size="fit"
              isTransparent={true}
              border={false}
              backgroundImg={showPassword ? searchIcon : searchNotIcon}
              onClick={togglePasswordVisibility}
            />
          </TitleGroup>
          <UniversalIconBtn
            size="small"
            border={false}
            isTransparent
            hoverOpacity
            backgroundImg={deleteIcon}
            onClick={() => clearModal()}
          />
        </HeaderRow>
        <PasswordWrapper>
          <HeadlessInput
            type={showPassword ? 'text' : 'password'}
            value={newPw}
            handleChange={(e) => changeNewPasswordInput(e.target.value)}
            placeholder={getModalMessage('AUTH_PASSWORD_LABEL')}
            StyledInput={MemberInfoValue}
          />
          <Space $height="20px">
          {error && <ErrorText>{error}</ErrorText>}
          </Space>
        </PasswordWrapper>
        <HeadlessBtn
          value={getBtnName('SET')}
          handleClick={handleSubmit}
          StyledBtn={SubmitButton}
        />
      </ChangeModalComp>
    </ChangeModalFrame>
  );
};

export const ChangeModalFrame = styled.div<{ $isClosing: boolean }>`
  position: relative;
  padding: 20px;
  width: 440px;
  height: fit-content;
  
  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) => ($isClosing ? 'scale(0.8)' : 'scale(1)')};

  transition: all 0.3s ease-in;
  ${paperTextureModal}
  ${popIn}
`;

const ChangeModalComp = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  padding: 0.5vw 1vw;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-bottom: 1.5vh;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const Title = styled.h2`
  ${({ theme }) => theme.typography.Body_02_1};
`;

const PasswordWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 18px;
`;

const ErrorText = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_alert_red};
  ${({ theme }) => theme.typography.Body_04};
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin-bottom: 10px;
`;

export default SetPWModal; 